import { Box, Divider, Group, Skeleton, Stack } from '@mantine/core';
import { useDebtor } from 'app/Private/Clients/Detail/client-atom';
import { Feedback } from 'app/Private/Clients/Detail/UnhandledInvoicesTab/Feedback';
import { Panel } from 'components/Panel';
import { Split } from 'components/Split';
import { useLoadUnhandledClient } from 'modules/AtRisk/api/use-load-unhandled-client';
import { usePrefetchNextDebtorAtRiskFromTab } from 'modules/AtRisk/api/use-prefetch-next-at-risk-debtor';
import { AiSummaryPanel, DebtorNotes } from '../InfoTab/components';
import { ClientSnapshot } from './ClientSnapshot';
import { UnhandledInvoices } from './UnhandledInvoices';
import { UnhandledInvoicesOverview } from './UnhandledInvoicesOverview';
import { ACTION_BUTTON_MIN_WIDTH } from './UnhandledinvoicesLayout';

export const UnhandledInvoicesTab = () => {
  usePrefetchNextDebtorAtRiskFromTab();

  const { id } = useDebtor();
  const { isUnhandledClientInitialLoading } = useLoadUnhandledClient({ debtorId: String(id) });

  if (isUnhandledClientInitialLoading) return <UnhandledInvoicesTabSkeleton />;

  return (
    <Stack pb="lg" mt="md">
      <UnhandledInvoicesOverview />
      <Split left={70}>
        <UnhandledInvoices />
        <Stack>
          <ClientSnapshot />
          <Feedback />
        </Stack>
      </Split>
      <Split>
        <DebtorNotes />
        <AiSummaryPanel />
      </Split>
    </Stack>
  );
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const ACTION_GROUP_SKELETONS = [
  { name: 'case-status', buttonCount: 4 },
  { name: 'dunning', buttonCount: 4 },
  { name: 'third-parties', buttonCount: 3 },
];

const UnhandledInvoicesTabSkeleton = () => (
  <Stack pb="lg" mt="md">
    {/* Overview bar */}
    <Panel pt={12} pb={18} pl={16}>
      <Group justify="space-between" mb="xs">
        <Group gap={6} align="baseline">
          <Skeleton height={14} width={70} />
          <Skeleton height={10} width={90} />
          <Skeleton height={14} width={70} />
          <Skeleton height={10} width={70} />
          <Skeleton height={10} width={50} />
        </Group>
        <Skeleton height={10} width={110} />
      </Group>
      <Skeleton height={12} radius="xl" />
    </Panel>

    <Split left={70}>
      {/* Left: action buttons + invoice list */}
      <Panel fullHeight pt={12}>
        {/* Action groups, each a label divider over a row of buttons */}
        <Stack gap="md">
          {ACTION_GROUP_SKELETONS.map(group => (
            <Stack key={group.name} gap="xs">
              <Divider labelPosition="left" label={<Skeleton height={8} width={110} />} />
              <Box
                display="grid"
                style={{
                  gridTemplateColumns: `repeat(auto-fill, minmax(${ACTION_BUTTON_MIN_WIDTH}px, 1fr))`,
                  gap: 'var(--mantine-spacing-md)',
                }}
              >
                {Array.from({ length: group.buttonCount }, (_, index) => index).map(index => (
                  <Skeleton
                    key={`action-button-skeleton-${group.name}-${index}`}
                    height={36}
                    radius={8}
                  />
                ))}
              </Box>
            </Stack>
          ))}
        </Stack>
        <Divider orientation="horizontal" my="md" />
        {/* Invoice list header */}
        <Box bdrs={8} bd="1px solid var(--mantine-color-gray-2)">
          <Box
            px={8}
            py={6}
            style={{
              borderBottom: '1px solid var(--mantine-color-gray-2)',
              borderRadius: '8px 8px 0px 0px',
            }}
          >
            <Group justify="space-between">
              <Skeleton height={10} width={60} />
              <Skeleton height={10} width={160} />
            </Group>
          </Box>
          {Array.from({ length: 3 }, (_, index) => index).map(index => (
            <Group
              key={`invoice-row-skeleton-${index}`}
              px={8}
              py={6}
              justify="space-between"
              style={{
                borderBottom: index < 2 ? '1px solid var(--mantine-color-gray-2)' : undefined,
              }}
            >
              <Group gap="xs">
                <Skeleton height={10} width={120} />
                <Skeleton height={10} width={80} />
              </Group>
              <Skeleton height={10} width={60} />
            </Group>
          ))}
        </Box>
      </Panel>

      {/* Right: client snapshot + feedback */}
      <Stack>
        <Panel pt={12}>
          {Array.from({ length: 5 }, (_, index) => index).map(index => (
            <Box key={`snapshot-row-skeleton-${index}`}>
              {index > 0 && <Divider my={8} />}
              <Group justify="space-between" py={4}>
                <Skeleton height={10} width={100} />
                <Stack gap={4} align="flex-end">
                  <Skeleton height={10} width={60} />
                  <Skeleton height={10} width={40} />
                </Stack>
              </Group>
            </Box>
          ))}
        </Panel>
        {/* Feedback placeholder */}
        <Panel>
          <Skeleton height={10} width={130} mb="xs" />
          <Skeleton height={32} radius={8} />
        </Panel>
      </Stack>
    </Split>

    <Split>
      {/* DebtorNotes placeholder */}
      <Panel>
        <Skeleton height={12} width={80} mb="sm" />
        <Skeleton height={70} radius={8} />
      </Panel>
      {/* AiSummaryPanel placeholder */}
      <Panel>
        <Skeleton height={10} width={100} mb="sm" />
        <Stack gap={6}>
          <Skeleton height={10} />
          <Skeleton height={10} />
          <Skeleton height={10} width="80%" />
        </Stack>
      </Panel>
    </Split>
  </Stack>
);
